import React, { useState, useMemo, useEffect } from 'react';
import { Table, Input, Button, notification, Space, Form, Modal, Radio, Checkbox, Tooltip, Switch, Pagination } from 'antd';
import { SaveOutlined, ExpandOutlined, CompressOutlined, ReloadOutlined } from '@ant-design/icons';
import { ComponentTitle } from '../../custom-components';
import useScreenSizeHook from '../../utils/useScreenSizeHook';
import { MetadataRecord } from '../hooks/useMetadata';
import { ColumnsType } from 'antd/es/table';
import { UnmatchedAssetInterface } from '../interfaces';
import { authAxios } from '../../utils/session_utils';
import styled from 'styled-components';
import { AssetCopyButton } from './CopyButton';

const StyledContainer = styled.div`
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Prevent content from spilling outside container */
  position: relative; /* Required for absolute positioning of pagination */
  
  .ant-table-container {
    height: calc(100% - 60px); /* Ensure enough space for pagination */
  }
  
  .ant-table {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  
  .ant-table-wrapper, .ant-spin-nested-loading, .ant-spin-container {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  
  .ant-pagination {
    display: flex !important;
    justify-content: flex-end !important;
  }
`;

interface MetadataPoolProps {
  fetchMetadata: () => void;
  selectedConflict: UnmatchedAssetInterface | null;
  onMetadataLink: (metadata: MetadataRecord, operation: string) => void;
  allMetadataData: MetadataRecord[];
  setAllMetadataData: React.Dispatch<React.SetStateAction<MetadataRecord[]>>;
  loading: boolean;
  onSaveAll: () => Promise<void>;
  externalSearchTerm?: string;
}

const MetadataPool: React.FC<MetadataPoolProps> = ({ 
  selectedConflict, 
  onMetadataLink, 
  allMetadataData,
  fetchMetadata, 
  setAllMetadataData,
  loading,
  onSaveAll,
  externalSearchTerm
}) => {
    const { height } = useScreenSizeHook();
    const [searchText, setSearchText] = useState('');
    const [editingKey, setEditingKey] = useState('');
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedResolution, setSelectedResolution] = useState<string | null>(null);
    const [selectedMetadata, setSelectedMetadata] = useState<MetadataRecord | null>(null);
    const [primarySelection, setPrimarySelection] = useState<string | null>(null);
    const [viewershipSelections, setViewershipSelections] = useState<string[]>([]);
    const [metadataSelections, setMetadataSelections] = useState<string[]>([]);
    const [showExpandedColumns, setShowExpandedColumns] = useState<boolean>(false);

    // Effect to handle external search terms
    useEffect(() => {
        if (externalSearchTerm && externalSearchTerm.trim()) {
            setSearchText(externalSearchTerm.trim());
        }
    }, [externalSearchTerm]);

    const isEditing = (record: MetadataRecord) => `${record.ref_id}-${record.id}` === editingKey;

    const edit = (record: MetadataRecord) => {
        form.setFieldsValue({ ...record });
        setEditingKey(`${record.ref_id}-${record.id}`);
    };

    const cancel = () => {
        setEditingKey('');
    };

    type MetadataRecordKey = keyof MetadataRecord;

    const updateMetadata = async (record: MetadataRecord) => {
      try {
          const row = await form.validateFields() as Partial<MetadataRecord>;
          
          // Identify changed fields
          const changedFields = (Object.keys(row) as MetadataRecordKey[]).reduce((acc, key) => {
              if (row[key] !== undefined && row[key] !== record[key]) {
                  acc[key] = row[key] as string;
              }
              return acc;
          }, {} as Partial<MetadataRecord>);
  
          // Add IDs to the changed fields
          const updatedRecord = {
              ...record,
              ...changedFields,
          };
  
          console.log(JSON.stringify(updatedRecord))
  
          const response = await authAxios({
              method: 'PUT',
              url: `${process.env.REACT_APP_BACK_END_API}/snowflake/metadata/partial`,
              data: updatedRecord,
          });
  
          if (response.status === 200) {
              // Update local state immediately
              setAllMetadataData(prevData => 
                prevData.map(item => item.id === updatedRecord.id ? updatedRecord : item)
              );

              notification.success({
                  message: 'Update Successful',
                  description: `Metadata for ${record.ref_id} has been updated.`,
              });
              setEditingKey('');
          } else {
              throw new Error('Update failed');
          }
      } catch (error) {
          console.error('Failed to update metadata:', error);
          notification.error({
              message: 'Update Failed',
              description: 'An error occurred while updating the metadata. Please try again.',
          });
      }
    };


    const normalizeText = (text: string): string => {
        return text
            .toLowerCase()
            .replace(/[""'']/g, '') // Remove various quote types
            .replace(/[^\w\s]/g, '') // Remove all special characters except letters, numbers, and spaces
            .replace(/\s+/g, '') // Remove all spaces
            .trim();
    };

    const calculateMatchScore = (item: MetadataRecord, searchTerm: string, normalizedSearchTerm: string): number => {
        const title = item.title || '';
        const refId = item.ref_id || '';
        let score = 0;
        
        // Exact ref_id match gets highest score
        if (refId.toLowerCase() === searchTerm) return 1000;
        
        // Exact title match gets very high score
        if (title.toLowerCase() === searchTerm) return 900;
        
        // Exact ref_id contains search term
        if (refId.toLowerCase().includes(searchTerm)) score += 800;
        
        // Exact title contains search term
        if (title.toLowerCase().includes(searchTerm)) score += 700;
        
        // Normalized title match (handles "Cigerettes" vs "Cigarettes")
        const normalizedTitle = normalizeText(title);
        if (normalizedTitle === normalizedSearchTerm) score += 600;
        if (normalizedTitle.includes(normalizedSearchTerm)) score += 500;
        
        // Word-by-word matching
        const searchWords = searchTerm.split(/\s+/).filter(word => word.length > 2);
        const titleWords = title.toLowerCase().split(/\s+/);
        
        if (searchWords.length > 0) {
            const matchedWords = searchWords.filter(searchWord => 
                titleWords.some(titleWord => titleWord.includes(searchWord))
            );
            const wordMatchRatio = matchedWords.length / searchWords.length;
            score += wordMatchRatio * 400;
        }
        
        return score;
    };

    const filteredData = useMemo(() => {
        if (!searchText.trim()) return allMetadataData;
        
        const searchTerm = searchText.toLowerCase().trim();
        const normalizedSearchTerm = normalizeText(searchText);
        
        const matchedItems = allMetadataData
            .map(item => ({
                ...item,
                matchScore: calculateMatchScore(item, searchTerm, normalizedSearchTerm)
            }))
            .filter(item => item.matchScore > 0)
            .sort((a, b) => b.matchScore - a.matchScore); // Sort by score descending
        
        return matchedItems;
    }, [allMetadataData, searchText]);

    const handleSearch = (value: string) => {
        setSearchText(value.trim());
    };

    const getUniqueValues = (data: MetadataRecord[], key: keyof MetadataRecord) => {
        const uniqueValues = new Set(data.map(item => item[key]?.toString().trim()));
        return Array.from(uniqueValues).filter(Boolean).map(value => ({ text: value, value: value }));
    };

    const handleLinkClick = (record: MetadataRecord) => {
      // Safety check - only proceed if selectedConflict is valid
      if (!selectedConflict || !selectedConflict.id) {
        notification.info({
          message: 'Action Required',
          description: 'Please select an unmatched asset first.'
        });
        return;
      }
      
      setSelectedMetadata(record);
      setIsModalVisible(true);
  };
  const handleModalOk = () => {
    if (selectedMetadata && primarySelection) {
        let resolution = '';
        if (primarySelection === '1') {
            if (viewershipSelections.includes('title') && viewershipSelections.includes('ref_id')) {
                resolution = 'update_viewership_title_and_ref_id';
            } else if (viewershipSelections.includes('title')) {
                resolution = 'update_viewership_title';
            } else if (viewershipSelections.includes('ref_id')) {
                resolution = 'update_viewership_ref_id';
            }
        } else if (primarySelection === '2') {
            if (metadataSelections.includes('title') && metadataSelections.includes('ref_id')) {
                resolution = 'update_metadata_title_and_ref_id';
            } else if (metadataSelections.includes('title')) {
                resolution = 'update_metadata_title';
            } else if (metadataSelections.includes('ref_id')) {
                resolution = 'update_metadata_ref_id';
            }
        }
        console.log('resolution: ',resolution)
        setSelectedResolution(resolution);
        onMetadataLink(selectedMetadata, resolution);
        setIsModalVisible(false);
        setPrimarySelection(null);
        setViewershipSelections([]);
        setMetadataSelections([]);
        setSelectedMetadata(null);
    }
};


const handleModalCancel = () => {
    setIsModalVisible(false);
    setPrimarySelection(null);
    setViewershipSelections([]);
    setMetadataSelections([]);
    setSelectedMetadata(null);
};



    const epStatusFilters = useMemo(() => getUniqueValues(allMetadataData, 'episode_status'), [allMetadataData]);
    const seriesStatusFilters = useMemo(() => getUniqueValues(allMetadataData, 'series_status'), [allMetadataData]);
    const seriesCodeFilters = useMemo(() => getUniqueValues(allMetadataData, 'series_code'), [allMetadataData]);
    const contentProviderFilters = useMemo(() => getUniqueValues(allMetadataData, 'content_provider'), [allMetadataData]);
    const languageFilters = useMemo(() => getUniqueValues(allMetadataData, 'language'), [allMetadataData]);

    const getBaseColumns = (): ColumnsType<MetadataRecord> => [
      {
        title: 'Ref ID',
        dataIndex: 'ref_id',
        key: 'ref_id',
        width: 120,
        ellipsis: true,
        sorter: (a, b) => a.ref_id.localeCompare(b.ref_id),
        render: (text) => (
          <Tooltip title={text || '-'}>
            <AssetCopyButton value={text || '-'} />
          </Tooltip>
        ),
      },
      {
          title: 'Title',
          dataIndex: 'title',
          key: 'title',
          width: showExpandedColumns ? undefined : 300, // Narrower to ensure action buttons are visible
          ellipsis: true,
          sorter: (a, b) => a.title.localeCompare(b.title),
          render: (text, record) => {
              const editable = isEditing(record);
              return editable ? (
                  <Form.Item
                      name="title"
                      style={{ margin: 0 }}
                      rules={[{ required: true, message: 'Title is required' }]}
                  >
                      <Input />
                  </Form.Item>
              ) : (
                  <Tooltip title={text || '-'}>
                    <AssetCopyButton value={text || '-'} />
                  </Tooltip>
              );
          },
      },
      {
          title: 'Series Code',
          dataIndex: 'series_code',
          key: 'series_code',
          width: showExpandedColumns ? undefined : 100,
          ellipsis: true,
          filters: seriesCodeFilters,
          onFilter: (value, record) => record.series_code?.toString().trim().toLowerCase() === value.toString().toLowerCase(),
          render: (text, record) => {
              const editable = isEditing(record);
              return editable ? (
                  <Form.Item
                      name="series_code"
                      style={{ margin: 0 }}
                  >
                      <Input />
                  </Form.Item>
              ) : (
                  <Tooltip title={text || '-'}>
                    <span>{text || '-'}</span>
                  </Tooltip>
              );
          },
      },
      {
          title: 'Language',
          dataIndex: 'language',
          width: showExpandedColumns ? undefined : 90,
          key: 'language',
          ellipsis: true,
          filters: languageFilters,
          onFilter: (value, record) => record.language?.toString().trim().toLowerCase() === value.toString().toLowerCase(),
          render: (text, record) => {
              const editable = isEditing(record);
              return editable ? (
                  <Form.Item
                      name="language"
                      style={{ margin: 0 }}
                  >
                      <Input />
                  </Form.Item>
              ) : (
                  <Tooltip title={text || '-'}>
                    <span>{text || '-'}</span>
                  </Tooltip>
              );
          },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120, // Updated width to accommodate Edit and Link buttons
        fixed: 'right', // Ensures actions column is always visible
        render: (_, record) => {
            const editable = isEditing(record);
            return (
                <Space size="middle">
                    {editable ? (
                        <>
                            <Button onClick={() => updateMetadata(record)} type="link">
                                Save
                            </Button>
                            <Button onClick={cancel} type="link">
                                Cancel
                            </Button>
                        </>
                    ) : (
                        <Button
                            onClick={() => edit(record)}
                            type="link"
                            disabled={editingKey !== ''}
                            title="Edit this metadata record"
                        >
                            Edit
                        </Button>
                    )}
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => handleLinkClick(record)}
                        disabled={!selectedConflict || !selectedConflict.id || editingKey !== ''}
                        title={!selectedConflict || !selectedConflict.id ? "Select an unmatched asset first" : "Link this metadata to the selected asset"}
                    >
                        Link
                    </Button>
                </Space>
            );
        },
      },
    ];
    
    const getExpandedColumns = (): ColumnsType<MetadataRecord> => [
      {
          title: 'Ep. Status',
          width: 100,
          dataIndex: 'episode_status',
          key: 'episode_status',
          filters: epStatusFilters,
          onFilter: (value, record) => record.episode_status?.toString().trim().toLowerCase() === value.toString().toLowerCase(),
          render: (text, record) => {
              const editable = isEditing(record);
              return editable ? (
                  <Form.Item
                      name="episode_status"
                      style={{ margin: 0 }}
                  >
                      <Input />
                  </Form.Item>
              ) : (
                  text
              );
          },
      },
      {
        title: 'Series Status',
        dataIndex: 'series_status',
        width: 100,
        key: 'series_status',
        filters: seriesStatusFilters,
        onFilter: (value, record) => record.series_status?.toString().trim().toLowerCase() === value.toString().toLowerCase(),
        render: (text, record) => {
            const editable = isEditing(record);
            return editable ? (
                <Form.Item
                    name="series_status"
                    style={{ margin: 0 }}
                >
                    <Input />
                </Form.Item>
            ) : (
                text
            );
        },
      },
      {
          title: 'Content Provider',
          dataIndex: 'content_provider',
          key: 'content_provider',
          width: 100,
          filters: contentProviderFilters,
          onFilter: (value, record) => record.content_provider?.toString().trim().toLowerCase() === value.toString().toLowerCase(),
          render: (text, record) => {
              const editable = isEditing(record);
              return editable ? (
                  <Form.Item
                      name="content_provider"
                      style={{ margin: 0 }}
                  >
                      <Input />
                  </Form.Item>
              ) : (
                  text
              );
          },
      },
    ];
    
    // Combine columns based on expanded state
    const columns: ColumnsType<MetadataRecord> = useMemo(() => {
        const baseColumns = getBaseColumns();
        if (showExpandedColumns) {
            return [
                ...baseColumns.slice(0, -1), // All except actions
                ...getExpandedColumns(),
                baseColumns[baseColumns.length - 1] // Actions column
            ];
        }
        return baseColumns;
    }, [showExpandedColumns, epStatusFilters, seriesStatusFilters, seriesCodeFilters, contentProviderFilters, languageFilters, selectedConflict, editingKey]);
    
    // State for custom pagination - reduce page size to show fewer rows
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(6); // Reduced from 10 to 6 to ensure pagination is visible
const isModalOkDisabled = !primarySelection || 
(primarySelection === '1' && viewershipSelections.length === 0) ||
(primarySelection === '2' && metadataSelections.length === 0);

    // Handle table pagination change
    const handleTableChange = (pagination: any) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };
    
    const handleSaveAll = async () => {
        try {
            await onSaveAll();
            notification.success({
                message: "Save Successful",
                description: "All records have been updated on the server.",
            });
        } catch (error) {
            notification.error({
                message: "Save Failed",
                description: "An error occurred while saving. Please try again.",
            });
        }
    };

    if (!Array.isArray(allMetadataData)) {
        console.error('allMetadataData is not an array:', allMetadataData);
        notification.error({
            message: "Data Error",
            description: "Unable to load metadata. Please try again later.",
        });
        return <div>Error: Unable to load metadata. Please try again later.</div>;
    }

    return (
      <StyledContainer>
      <Form form={form} component={false}>
          <ComponentTitle>
              Metadata Pool
              {showExpandedColumns && (
                  <span style={{ fontSize: '13px', fontWeight: 'normal', marginLeft: '12px', color: '#1890ff' }}>
                      (With Status Columns)
                  </span>
              )}
          </ComponentTitle>
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                  <Input.Search
                      placeholder="Search by title or Ref ID"
                      onSearch={handleSearch}
                      style={{ width: 300 }}
                  />
                  <Button onClick={fetchMetadata} icon={<ReloadOutlined />}>Reload</Button>
                  <Button
                      type={showExpandedColumns ? "primary" : "default"}
                      size="middle"
                      icon={showExpandedColumns ? <CompressOutlined /> : <ExpandOutlined />}
                      onClick={() => setShowExpandedColumns(!showExpandedColumns)}
                      style={{ 
                          borderColor: showExpandedColumns ? '#1890ff' : '#1890ff',
                          backgroundColor: showExpandedColumns ? '#1890ff' : 'white',
                          color: showExpandedColumns ? 'white' : '#1890ff'
                      }}
                  >
                      {showExpandedColumns ? "Hide Status Columns" : "Show Status Columns"}
                  </Button>
              </Space>
              <Button 
                  icon={<SaveOutlined />} 
                  onClick={handleSaveAll}
                  type="primary"
                  disabled={!selectedConflict}
              >
                  Process Linked Records
              </Button>
          </div>
          <div style={{ marginBottom: '10px' }}>
              {!selectedConflict && (
                  <div style={{ color: '#1890ff', fontStyle: 'italic' }}>
                      Select an unmatched asset from the table on the left to link it with metadata.
                  </div>
              )}
          </div>
          <div style={{ marginBottom: '50px', flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Table
                columns={columns}
                dataSource={filteredData.slice((currentPage-1) * pageSize, currentPage * pageSize)}
                loading={loading}
                pagination={false} // We'll handle pagination separately
                scroll={{ y: 'calc(100vh - 332px)', x: 'max-content' }}
                rowKey={(record) => `${record.ref_id}-${record.id}`}
                locale={{
                    emptyText: loading ? 'Loading...' : 'No data available',
                }}
                onChange={handleTableChange}
            />
          </div>
          
          {/* Add fixed pagination at the bottom */}
          <div style={{ 
             position: 'absolute',
             bottom: 0,
             left: 0,
             right: 0,
             paddingTop: '12px',
             paddingBottom: '8px',
             borderTop: '1px solid #d9d9d9',
             display: 'flex',
             justifyContent: 'flex-end',
             backgroundColor: '#f0f2f5',
             paddingLeft: '16px',
             paddingRight: '16px',
             borderBottomLeftRadius: '4px',
             borderBottomRightRadius: '4px',
             boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.05)',
             zIndex: 10
          }}>
             <Pagination
                 showSizeChanger
                 size="small"
                 current={currentPage}
                 pageSize={pageSize}
                 total={filteredData.length}
                 onChange={(page, size) => {
                     setCurrentPage(page);
                     if (size) setPageSize(size);
                 }}
                 pageSizeOptions={['6', '10', '20']}
                 showTotal={(total) => `Total: ${total}`}
             />
          </div>
            <Modal
                title="Select Resolution"
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okButtonProps={{ disabled: isModalOkDisabled }}
            >
                <Radio.Group onChange={(e) => setPrimarySelection(e.target.value)} value={primarySelection}>
                    <Space direction="vertical">
                        <Radio value="1">1. Update viewership record</Radio>
                        <Radio value="2">2. Update metadata record</Radio>
                    </Space>
                </Radio.Group>

                {primarySelection === '1' && (
                    <Checkbox.Group
                        style={{ marginTop: 16, marginLeft: 24 }}
                        value={viewershipSelections}
                        onChange={setViewershipSelections as any}
                    >
                        <Space direction="vertical">
                            <Checkbox value="title">a. Title</Checkbox>
                            <Checkbox value="ref_id">b. Ref_Id</Checkbox>
                        </Space>
                    </Checkbox.Group>
                )}

                {primarySelection === '2' && (
                    <Checkbox.Group
                        style={{ marginTop: 16, marginLeft: 24 }}
                        value={metadataSelections}
                        onChange={setMetadataSelections as any}
                    >
                        <Space direction="vertical">
                            <Checkbox value="title">a. Title</Checkbox>
                            <Checkbox value="ref_id">b. Ref_Id</Checkbox>
                        </Space>
                    </Checkbox.Group>
                )}
            </Modal>
      </Form>
      </StyledContainer>
    );
};

export default MetadataPool;



